import { isNil } from 'lodash/fp';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
    type DealerListDataFragment,
    type FinderApplicationEntrypointContextDataFragment,
    type FinderApplicationPublicAccessEntrypointContextDataFragment,
    type FinderVehicleDataFragment,
} from '../../../../api/fragments';
import { useDraftFinderApplicationMutation } from '../../../../api/mutations/draftFinderApplication';
import { useLanguage } from '../../../../components/contexts/LanguageContextManager';
import PortalLoadingElement from '../../../../components/PortalLoadingElement';
import { type FinderModule, useFinderApplicationContext } from '../FinderApplicationContext';
import OfferPage from '../OfferPage';
import { JourneyStage } from '../types';

type FinderJourneyPathProps = {
    endpoint:
        | FinderApplicationEntrypointContextDataFragment
        | FinderApplicationPublicAccessEntrypointContextDataFragment;
};

const useDraftTestDriveFinderApplication = () => {
    const [draftFinderApplication] = useDraftFinderApplicationMutation();

    return useCallback(
        (
            vehicle: FinderVehicleDataFragment,
            module: FinderModule,
            dealer: DealerListDataFragment,
            endpoint:
                | FinderApplicationEntrypointContextDataFragment
                | FinderApplicationPublicAccessEntrypointContextDataFragment,
            languageId: string
        ) =>
            draftFinderApplication({
                variables: {
                    customer: {
                        newLocalCustomer: { fields: [] },
                    },
                    configuration: {
                        withFinancing: false,
                        withInsurance: false,
                        tradeIn: false,
                        testDrive: true,
                        requestForFinancing: false,
                    },
                    dealerId: dealer.id,
                    moduleId: module.id,
                    vehicle: {
                        existingSimpleLocalVehicleId: vehicle.id,
                    },
                    insurancing: null,
                    promoCodeId: null,
                    endpointId: endpoint.id,
                    languageId,
                    financing: null,
                    tradeInVehicle: [],
                    isTestDriveDrafting: true,
                },
            }),
        [draftFinderApplication]
    );
};
const FinderJourneyPath = ({ endpoint }: FinderJourneyPathProps) => {
    const { vehicle, module, dealer, endpoint: finderEndpoint } = useFinderApplicationContext();
    const { currentLanguageId } = useLanguage();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();

    const isParamPathTestDrive = searchParams.get('path') === 'testdrive';

    const [draftedToken, setDraftedToken] = useState<string>(null);
    const draftApplication = useDraftTestDriveFinderApplication();
    useEffect(() => {
        // draft application
        if (isParamPathTestDrive) {
            draftApplication(vehicle, module, dealer, finderEndpoint, currentLanguageId).then(result => {
                setDraftedToken(result.data.result.token);

                // public access and trigger via test drive button from Finder site
            });
        }
    }, [
        currentLanguageId,
        dealer,
        draftApplication,
        draftedToken,
        finderEndpoint,
        isParamPathTestDrive,
        module,
        navigate,
        vehicle,
    ]);

    if (!isNil(draftedToken)) {
        navigate('./apply', { state: { token: draftedToken, stage: JourneyStage.Initialize } });
    }

    if (!isParamPathTestDrive) {
        return <OfferPage endpoint={endpoint} />;
    }

    return <PortalLoadingElement />;
};

export default FinderJourneyPath;
